import type { OptimizationRun, Item3D } from '../types/optimization'
import { getBaseItemId } from './optimizationUtils'
import { cargoLineLabels, palletDisplayLabels } from './cargoLinePresentation'
import { formatMeasurement, measurementUnit } from './measurement'
import type { MeasurementSystem } from './measurementSystem'

/** Geometry is normalized in mm and kg, including in imperial projects. */
export function cargoHintDetails(unit: { item_id?: string; description?: string; length?: number; width?: number; height?: number; weight?: number; unloading_order?: number | null; loading_order?: number | null; loadingOrder?: number | null }, system: MeasurementSystem = 'metric') {
  const dimensionUnit = system === 'imperial' ? 'in' : 'cm'
  const valid = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0
  const dims = [unit.length, unit.width, unit.height]
  const volume = dims.every(n => valid(n) && n > 0) ? dims.reduce<number>((v, n) => v * n!, 1) : null
  return {
    description: unit.description?.trim() || 'Description non renseignée',
    dimensions: dims.map(n => formatMeasurement(valid(n) && n > 0 ? n : null, 'mm', dimensionUnit, { includeUnit: false, maximumFractionDigits: 2 })).join(' × ') + ` ${dimensionUnit}`,
    volume: formatMeasurement(volume, 'mm3', measurementUnit(system, 'volume'), { maximumFractionDigits: 3 }),
    weight: formatMeasurement(valid(unit.weight) ? unit.weight : null, 'kg', measurementUnit(system, 'mass'), { maximumFractionDigits: 2 }),
    reference: unit.item_id || '—',
    unloadingOrder: valid(unit.unloading_order) ? String(unit.unloading_order) : undefined,
    loadingOrder: Number.isInteger(unit.loading_order ?? unit.loadingOrder) && (unit.loading_order ?? unit.loadingOrder)! > 0 ? String(unit.loading_order ?? unit.loadingOrder) : undefined,
  }
}

/** Display identifiers only: operational IDs are always kept intact. */
export function cargoDisplayId(value: string, sequence?: number): string {
  const compact = String(value || '').replace(/^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}(?:_|$)/i, '')
  return compact || (sequence == null ? 'Colis' : String(sequence).padStart(3, '0'))
}

export function cargoLabel(id: string, description?: string, sequence?: number): string {
  const name = description?.trim() || 'Colis'
  const suffix = Number(id.match(/_(\d+)$/)?.[1])
  const order = sequence ?? (Number.isInteger(suffix) && suffix > 0 ? suffix : 1)
  return `${name}_${String(order).padStart(3, '0')}`
}

/** Stable display-only package numbers, shared by labels, hints and alert actions. */
export function withCargoSceneLabels(run: OptimizationRun, cargoLines: Item3D[] = run.input.items): OptimizationRun {
  const placements: any[] = run.result?.placements || []
  if (!placements.length || placements.every(placement => placement.metadata?.viewer_cargo_label)) return run
  const lines = new Map(cargoLines.map(item => [item.id, item]))
  const knownIds = new Set(lines.keys())
  const lineLabels = cargoLineLabels(cargoLines)
  const used = new Map<string, Set<number>>()
  const nameOf = (line: any) => String(line?.description || 'Colis').trim() || 'Colis'
  const labelFor = (unit: any) => {
    const id = String(unit.item_id ?? unit.itemId ?? unit.id ?? '')
    const explicitSource = unit.source_id ?? unit.metadata?.source_id
    const line = lines.get(explicitSource) ?? lines.get(getBaseItemId(id, knownIds))
    const name = line?.description?.trim() ? lineLabels.get(line.id) ?? nameOf(line) : nameOf(line ?? unit)
    const quantity = Math.max(1, Number(line?.quantity) || 1)
    const suffix = Number(id.match(/_(\d+)$/)?.[1])
    const localOrder = line?.id !== id && Number.isInteger(suffix) && suffix > 0 && suffix <= quantity ? suffix : 1
    const lineKey = line?.id ?? name
    const numbers = used.get(lineKey) ?? new Set<number>()
    let order = localOrder
    while (numbers.has(order)) order++
    numbers.add(order); used.set(lineKey, numbers)
    return cargoLabel(id, name, order)
  }
  const originalGroups: any[] = run.result?.palletGroups ?? run.result?.pallet_groups ?? []
  const palletNames = palletDisplayLabels({ ...run, input: { ...run.input, items: cargoLines } })
  const loadedIds = new Set(placements.map(unit => unit.item_id ?? unit.itemId))
  const groups = originalGroups.map(group => ({ ...group, children_positions: (group.children_positions || []).map((child: any) => ({
    ...child, viewer_cargo_label: loadedIds.has(group.pallet_id) ? labelFor(child) : undefined,
  })) }))
  const groupIds = new Set(groups.map(group => group.pallet_id))
  return { ...run, result: { ...run.result!, palletGroups: groups, pallet_groups: groups,
    placements: placements.map(unit => ({ ...unit, metadata: { ...unit.metadata,
      viewer_cargo_label: groupIds.has(unit.item_id ?? unit.itemId)
        ? palletNames.get(unit.item_id ?? unit.itemId) ?? 'Palette' : labelFor(unit),
    } })),
  } }
}

/** Both the legacy snapshot and the output-summary contract can reference several units. */
export function alertItemIds(alert: any): string[] {
  if (!alert || typeof alert !== 'object') return []
  // Older normalized summaries retain paired references as scalar parameters.
  const values = [alert, alert.parameters].filter(Boolean).flatMap(source => [
    source.item_ids, source.itemIds, source.affected_items, source.affectedItems,
    source.item_id, source.itemId, source.item1Id, source.item2Id, source.item1_id, source.item2_id,
    source.item1, source.item2, source.hazardousItem, source.nonHazardousItem, source.supportingItemId,
    ...(Array.isArray(source.itemsAbove) ? source.itemsAbove.map((item: any) =>
      typeof item === 'object' && item ? item.item_id ?? item.id : item) : []),
  ])
  return [...new Set(values.flatMap(value => Array.isArray(value) ? value : [value])
    .filter(value => typeof value === 'string' || typeof value === 'number').map(String))]
}

export interface AlertCargoItem { id: string; label: string }

/** Resolve alert references against the actual scene, including article and pallet-child aliases. */
export function resolveAlertCargo(alert: any, run: any): AlertCargoItem[] {
  const references = new Set(alertItemIds(alert))
  if (!references.size) return []
  const inputItems = new Map<string, any>((run?.input?.items || []).map((item: any) => [item.id, item]))
  const pallets = new Map<string, any>((run?.result?.pallet_groups ?? run?.result?.palletGroups ?? [])
    .map((pallet: any) => [pallet.pallet_id, pallet]))
  const resolved = new Map<string, AlertCargoItem>()
  const matches = (aliases: any[]) => aliases.some(alias => alias != null && references.has(String(alias)))
  for (const placement of run?.result?.placements || []) {
    const id = placement.item_id || placement.itemId || placement.id
    if (!id) continue
    const baseId = String(id).replace(/_\d+$/, '')
    const sourceId = placement.source_id ?? placement.metadata?.source_id ?? (inputItems.has(id) ? id : baseId)
    const item = inputItems.get(id) ?? inputItems.get(sourceId)
    const pallet = pallets.get(id)
    if (matches([id, placement.id, placement.itemId, placement.item_reference, placement.itemReference,
      placement.reference, sourceId, item?.reference, item?.sku])) {
      resolved.set(id, { id, label: placement.metadata?.viewer_cargo_label ?? cargoLabel(id, item?.description) })
      continue
    }
    for (const child of pallet?.children_positions ?? item?.pallet_children ?? []) {
      if (child.item_id && matches([child.item_id, child.source_id])) {
        resolved.set(child.item_id, { id: child.item_id, label: child.viewer_cargo_label ?? cargoLabel(child.item_id, child.description) })
      }
    }
  }
  return [...resolved.values()]
}

/** Decorative lines and text must never steal a hit from a carton's physical surface. */
export const ignoreCargoRaycast = () => undefined

export function canvasPointerCoordinates(event: { clientX: number; clientY: number }, bounds: {
  left: number; top: number; width: number; height: number
}): [number, number] {
  return [(event.clientX - bounds.left) / bounds.width * 2 - 1,
    -(event.clientY - bounds.top) / bounds.height * 2 + 1]
}

